import { BigNumber } from "ethers"
import { commify, formatUnits } from "ethers/lib/utils"
import { t } from "i18next"
import { filter } from "lodash"
import { useCallback, useContext, useMemo } from "react"
import { enqueueToast } from "src/components/Toastify"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import {
  PoolName,
  FER,
  POOLS_MAP,
  REWARD_TOKENS,
  FARMV2_GAS_PRICE,
  FARMV2_GAS_LIMIT,
} from "src/constants"
import { getAddress } from "src/utils/addressHelpers"
import { BIG_ZERO } from "src/constants/bigNumber"
import useFerroBoostHandlers from "src/pages/Stake/hooks/useFerroBoostHandlers"
import { RewardsBalancesContext } from "src/providers/RewardsBalancesProvider"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { useUserFerroBoost } from "src/state/xFer/hooks"
import { formatBNToString, getChainId } from "src/utils"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import { useActiveWeb3React } from "."
import { useMiniChefContract, useFerroFarmV2Contract } from "./useContract"

export function useClaimReward(
  amount: BigNumber,
  poolName?: PoolName,
  rewardsV2?: {
    tokens: string[]
    earnings: BigNumber[]
  },
) {
  const { chainId, account } = useActiveWeb3React()
  const farmContract = useMiniChefContract()
  const farmV2Contract = useFerroFarmV2Contract()
  const { addTransactions } = useTransactionModal()

  const summary: TransactionSummaryData = useMemo(
    () => [
      {
        label: t("Claim"),
        token: FER,
        amount: commify(formatBNToString(amount ?? BIG_ZERO, FER.decimals, 2)),
      },
    ],
    [amount],
  )
  const summaryV2: TransactionSummaryData = useMemo(() => {
    const sum: TransactionSummaryData = []
    rewardsV2?.tokens?.forEach((address, index) => {
      const token = REWARD_TOKENS.find(
        (t) => getAddress(t.addresses) === address,
      )
      if (token) {
        const earning = rewardsV2.earnings[index]
        if (index < rewardsV2.earnings.length - 1 || earning.gt(0)) {
          sum.push({
            label: t("Claim"),
            token,
            amount: commify(
              formatBNToString(earning ?? BIG_ZERO, token.decimals, 2),
            ),
          })
        }
      }
    })
    return sum
  }, [rewardsV2])

  const pool = poolName ? POOLS_MAP[poolName] : undefined

  const claimPoolReward = useCallback(async () => {
    if (!chainId || !account || !pool) return
    try {
      const pid = pool.rewardPids[chainId]
      if (pid === null) return
      if (pool.version === 2) {
        if (!farmV2Contract) return
        const txnV2 = async () =>
          await farmV2Contract.deposit(pid, BIG_ZERO, {
            gasLimit: FARMV2_GAS_LIMIT,
            gasPrice: FARMV2_GAS_PRICE,
          })
        await addTransactions([txnV2], summaryV2, undefined, true)
      } else {
        if (!farmContract) return
        const txn = async () => await farmContract.deposit(pid, BIG_ZERO)
        await addTransactions([txn], summary, undefined, true)
      }
    } catch (e) {
      enqueueToast("error", "Unable to claim reward")
      throw e
    }
  }, [
    account,
    addTransactions,
    chainId,
    pool,
    farmContract,
    farmV2Contract,
    summary,
    summaryV2,
  ])

  const wrappedClaim = async () => {
    await claimPoolReward()
  }

  return {
    claim: wrappedClaim,
  }
}

export function useClaimVaultReward() {
  const { boostData } = useUserFerroBoost()
  const { onDeposit } = useFerroBoostHandlers()

  const summary: TransactionSummaryData = [
    {
      label: "Harvest",
      amount: formatNumber(
        formatUnits(boostData.pendingFer, FER.decimals),
        2,
        6,
      ),
      token: FER,
    },
  ]

  const claimVaultReward = async () => {
    try {
      await onDeposit("0", "0", summary)
    } catch (e) {
      enqueueToast("error", "Please try again later", "Transaction Failed")
      throw e
    }
  }

  const wrappedClaim = async () => {
    await claimVaultReward()
  }

  return { claim: wrappedClaim }
}

export const useClaimAllReward = () => {
  const chainId = getChainId()
  const farmContract = useMiniChefContract()
  const farmV2Contract = useFerroFarmV2Contract()
  const { addTransactions } = useTransactionModal()
  const [rewards] = useContext(RewardsBalancesContext)

  const pools = filter(POOLS_MAP, (pool) => !!pool.addresses[chainId])

  const claimAll = async () => {
    // TODO: Review here
    if (!farmContract) return

    const claimablePools = pools.filter(
      (pool) =>
        pool.rewardPids[chainId] != null &&
        rewards[pool.name] != undefined &&
        rewards[pool.name].gt(0),
    )
    const transactions = claimablePools.map((pool) => () => {
      if (pool.version === 2 && farmV2Contract) {
        return farmV2Contract.deposit(
          pool.rewardPids[chainId] as number,
          BIG_ZERO,
        )
      } else {
        return farmContract.deposit(
          pool.rewardPids[chainId] as number,
          BIG_ZERO,
        )
      }
    })

    const summary = claimablePools.map((pool) => ({
      label: `Claim ${pool.name}`,
      token: FER,
      amount: commify(formatBNToString(rewards[pool.name], FER.decimals, 2)),
    }))

    try {
      await addTransactions(transactions, summary, true)
    } catch (e) {
      enqueueToast("error", "Unable to claim reward")
      throw e
    }
  }

  const wrappedClaim = async () => {
    await claimAll()
  }

  return { claim: wrappedClaim }
}
